import { ArrowUpRight, Download, FileText } from "lucide-react";
import { CornerMarks, StatusLED } from "@/components/infra";
import { SIGNALS } from "@/lib/wireData";

const SignalChart = ({ chart }) => {
  const max = Math.max(...chart.points.map((p) => p.value));
  return (
    <div data-testid="signal-chart" className="chrome-surface relative p-5 md:p-7">
      <CornerMarks color="#A9ADB4" />
      <div className="font-mono-label text-[10px] tracking-[0.18em] uppercase text-[#0F1117] mb-1">
        {chart.title}
      </div>
      <div className="text-[12px] text-[#71747B] mb-6">{chart.subtitle}</div>
      <div className="grid grid-cols-3 gap-4 md:gap-6 items-end h-44 md:h-52">
        {chart.points.map((p) => (
          <div key={p.label} className="flex flex-col justify-end h-full">
            <div
              className={`font-display text-lg md:text-2xl font-extrabold tracking-tight leading-none mb-2 ${
                p.hot ? "text-[#0AAE6E]" : "text-[#0F1117]"
              }`}
            >
              {p.display}
            </div>
            <div
              className="w-full"
              style={{
                height: `${(p.value / max) * 100}%`,
                minHeight: 6,
                background: p.hot
                  ? "linear-gradient(180deg, #0FBF77 0%, #0AAE6E 100%)"
                  : "linear-gradient(180deg, #3D4049 0%, #22252C 100%)",
                boxShadow: p.hot ? "0 0 22px rgba(15,191,119,0.45)" : "none",
              }}
            />
          </div>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-4 md:gap-6 mt-3 border-t border-[#D2D5DA] pt-3">
        {chart.points.map((p) => (
          <div key={p.label}>
            <div className="font-mono-label text-[9.5px] tracking-[0.16em] uppercase text-[#0F1117]">
              {p.label}
            </div>
            <div className="text-[11px] text-[#71747B] leading-snug mt-0.5">{p.note}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

const SignalStory = ({ s }) => (
  <article
    id={`signal-${s.slug}`}
    data-testid={`wire-signal-${s.slug}`}
    className="grid grid-cols-12 gap-y-8 gap-x-0 sm:gap-x-8 lg:gap-12 scroll-mt-28"
  >
    <div className="col-span-12 lg:col-span-7">
      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-6">
        <span className="inline-flex items-center gap-2.5 font-mono-label text-[10px] tracking-[0.22em] uppercase text-[#0F1117]">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full bg-[#0FBF77] opacity-60" />
            <span className="relative inline-flex h-2 w-2 laser-dot" />
          </span>
          {s.kicker} · {s.date}
        </span>
        {s.meta && (
          <span className="font-mono-label text-[10px] tracking-[0.16em] uppercase text-[#71747B]">
            {s.meta}
          </span>
        )}
      </div>

      <h2
        data-testid={`signal-headline-${s.slug}`}
        className="font-display text-3xl sm:text-4xl lg:text-5xl leading-[1.02] tracking-tight font-extrabold text-[#0F1117] text-balance mb-5"
      >
        {s.headline}
      </h2>
      <p className="font-display text-lg sm:text-xl font-extrabold tracking-tight text-[#0F1117] leading-snug mb-7">
        {s.dek}
      </p>

      <div className="space-y-4 text-base md:text-[17px] text-[#3D4049] leading-relaxed max-w-2xl">
        {s.body.map((p, i) => (
          <p key={i}>{p}</p>
        ))}
      </div>

      <blockquote
        data-testid={`signal-take-${s.slug}`}
        className="mt-8 border-l-2 border-l-[#0FBF77] pl-5 md:pl-6"
      >
        <div className="font-mono-label text-[10px] tracking-[0.2em] uppercase text-[#0AAE6E] mb-2">
          The DCYB take
        </div>
        <p className="font-display text-lg md:text-xl font-extrabold tracking-tight text-[#0F1117] leading-snug">
          {s.take}
        </p>
      </blockquote>

      <div
        data-testid={`signal-stats-${s.slug}`}
        className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 border-l border-t hairline mt-10"
      >
        {s.stats.map(([value, label, note]) => (
          <div
            key={label}
            className="border-r border-b hairline px-4 py-5 bg-gradient-to-b from-white to-[#F4F5F7]"
          >
            <div className="font-display text-2xl md:text-[26px] font-extrabold tracking-tight text-[#0F1117] leading-none mb-2">
              {value}
            </div>
            <div className="font-mono-label text-[9px] tracking-[0.16em] uppercase text-[#0F1117]">
              {label}
            </div>
            <div className="text-[11px] text-[#71747B] leading-snug mt-1">{note}</div>
          </div>
        ))}
      </div>

      <div className="mt-8 flex flex-wrap gap-3">
        <a
          href={s.study.pdf}
          target="_blank"
          rel="noopener noreferrer"
          data-testid={`signal-study-pdf-${s.slug}`}
          className="group chrome-btn-dark chrome-btn-dark-glow inline-flex items-center gap-2.5 font-mono-label text-[11px] tracking-[0.16em] px-6 py-3.5"
        >
          <FileText size={14} />
          Read the study{s.study.pages ? ` · ${s.study.pages} pp` : ""}
          <ArrowUpRight
            size={14}
            className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
          />
        </a>
        {s.source && (
          <a
            href={s.source.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`signal-source-${s.slug}`}
            className="chrome-btn inline-flex items-center gap-2 font-mono-label text-[11px] tracking-[0.14em] px-6 py-3.5"
          >
            {s.source.label}
            <ArrowUpRight size={14} />
          </a>
        )}
        {s.image.downloadLabel && (
          <a
            href={s.image.src}
            download
            data-testid={`signal-image-download-${s.slug}`}
            className="inline-flex items-center gap-2 font-mono-label text-[10.5px] tracking-[0.16em] uppercase text-[#5F6168] hover:text-[#0F1117] px-2 py-3.5 transition-colors"
          >
            <Download size={13} />
            {s.image.downloadLabel}
          </a>
        )}
      </div>

      <p className="mt-6 font-mono-label text-[9px] tracking-[0.16em] uppercase text-[#8A8D94]">
        Sources: {s.sources} · {s.study.author}
      </p>
    </div>

    <div className="col-span-12 lg:col-span-5 space-y-6">
      <a
        href={s.image.src}
        target="_blank"
        rel="noopener noreferrer"
        data-testid={`signal-image-${s.slug}`}
        className="group chrome-surface relative block p-3 md:p-4 transition-all duration-300 hover:shadow-[0_24px_60px_rgba(15,17,23,0.12)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0F1117]"
      >
        <CornerMarks color="#A9ADB4" />
        <img
          src={s.image.src}
          alt={s.image.alt}
          loading="lazy"
          className="w-full h-auto border border-[#D2D5DA] bg-white"
          draggable="false"
        />
        <div className="flex items-center justify-between mt-3 px-1">
          <span className="font-mono-label text-[9.5px] tracking-[0.16em] uppercase text-[#5F6168] group-hover:text-[#0F1117] transition-colors">
            {s.image.label}
          </span>
          <ArrowUpRight
            size={13}
            className="text-[#71747B] transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5 group-hover:text-[#0F1117]"
          />
        </div>
      </a>
      {s.chart && <SignalChart chart={s.chart} />}
    </div>
  </article>
);

export const WireSignals = () => (
  <section id="signals" className="border-b hairline bg-white relative overflow-hidden scroll-mt-20">
    <div
      aria-hidden
      className="absolute inset-0 pointer-events-none opacity-[0.03]"
      style={{
        backgroundImage:
          "linear-gradient(0deg, #0F1117 1px, transparent 1px), linear-gradient(90deg, #0F1117 1px, transparent 1px)",
        backgroundSize: "44px 44px",
      }}
    />
    <div className="relative max-w-7xl mx-auto px-6 md:px-12 py-14 md:py-20">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-10">
        <div className="font-mono-label text-[11px] text-[#0F1117] flex items-center gap-3">
          <StatusLED />
          Industry signals · DCYB original research · {SIGNALS.length} studies
        </div>
        <span className="font-mono-label text-[9.5px] tracking-[0.18em] uppercase text-[#71747B]">
          The definitive source for the next generation of AI and digital infrastructure
        </span>
      </div>
      <div className="divide-y divide-[#D2D5DA]">
        {SIGNALS.map((s, i) => (
          <div key={s.slug} className={i === 0 ? "pb-16 md:pb-20" : "pt-16 md:pt-20"}>
            <SignalStory s={s} />
          </div>
        ))}
      </div>
    </div>
  </section>
);

export default WireSignals;
